import { z } from "zod";
import { DateTimeWithOffset } from "@/lib/api/schemas/patterns";
import { ProjectType } from "@/lib/domain/platform";
import { Auth } from "../auth";
import type { ActionReturnType } from "../types";
import { request, validateInput } from "../request";

const ProjectsListParameters = z.object({
  client_id: z.number().nullish(),
  search: z.string().nullish(),
  ordering: z.string().nullish(),
  limit: z.number().int().min(1).nullish(),
  offset: z.number().int().min(0).nullish(),
});

class ProjectsEndpoint {
  public static readonly basePath = "projects";
  public static readonly schemas = {
    list: {
      parameters: ProjectsListParameters,
      response: z.object({
        count: z.number(),
        items: z
          .object({
            id: z.number(),
            created_at: DateTimeWithOffset,
            name: z.string(),
            key: z.string(),
            default: z.boolean(),
            client_id: z.number(),
            type: ProjectType,
            suits_count: z.number(),
            cases_count: z.number(),
          })
          .array(),
      }),
    },
    create: {
      body: z.object({
        name: z.string(),
        key: z.string(),
        default: z.boolean(),
        type: ProjectType.optional(),
      }),
    },
    read: {
      response: z.object({
        id: z.number(),
        created_at: DateTimeWithOffset,
        name: z.string(),
        key: z.string(),
        default: z.boolean(),
        client_id: z.number(),
        type: ProjectType,
      }),
    },
    update: {
      body: z.object({
        name: z.string(),
        key: z.string(),
        default: z.boolean().default(false),
      }),
    },
  };

  constructor(private api: Auth) {}

  public async list(
    query: z.input<typeof ProjectsEndpoint.schemas.list.parameters> = {},
  ): ActionReturnType<z.infer<typeof ProjectsEndpoint.schemas.list.response>> {
    const params = validateInput(ProjectsEndpoint.schemas.list.parameters, query);
    if (!params.ok) return params;
    return request({
      log: ["Projects", "list"],
      method: "get",
      path: [ProjectsEndpoint.basePath],
      query: params.data,
      response: ProjectsEndpoint.schemas.list.response,
    });
  }

  public async create(
    body: z.input<typeof ProjectsEndpoint.schemas.create.body>,
    query?: { client_id: string }, // should be automatic but for admins there is no profile hence there is no client_id and we have to pass it manually
  ): ActionReturnType<undefined> {
    const bodyValidated = validateInput(ProjectsEndpoint.schemas.create.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Projects", "create"],
      method: "post",
      path: [ProjectsEndpoint.basePath],
      query,
      json: bodyValidated.data,
    });
  }

  public read(id: string | number): ActionReturnType<z.infer<typeof ProjectsEndpoint.schemas.read.response>> {
    return request({
      log: ["Projects", "read"],
      method: "get",
      path: [ProjectsEndpoint.basePath, id],
      response: ProjectsEndpoint.schemas.read.response,
    });
  }

  public async update(
    id: string,
    body: z.input<typeof ProjectsEndpoint.schemas.update.body>,
  ): ActionReturnType<undefined> {
    const bodyValidated = validateInput(ProjectsEndpoint.schemas.update.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Projects", "update"],
      method: "put",
      path: [ProjectsEndpoint.basePath, id],
      json: bodyValidated.data,
    });
  }

  public delete(id: string): ActionReturnType<undefined> {
    return request({
      log: ["Projects", "delete"],
      method: "delete",
      path: [ProjectsEndpoint.basePath, id],
    });
  }
}

export { ProjectsEndpoint };
